import { StaleBadge } from "@components/campaigns/detail/lineage/StaleBadge";
import { VersionChip } from "@components/campaigns/detail/lineage/VersionChip";
import { OriginBadge } from "@components/shared/OriginBadge";
import { RegenerateConfirmDialog } from "@components/shared/RegenerateConfirmDialog";
import { SlideOverPanel } from "@components/shared/SlideOverPanel";
import {
  ASSESS_CANDIDATE,
  REGENERATE_CAMPAIGN_DECISION_SUMMARY,
  REGENERATE_INTERVIEW_ROUND_SYNTHESIS,
} from "@lib/graphql/mutations";
import {
  ASSESSMENT_PROVENANCE_QUERY,
  DECISION_SUMMARY_PROVENANCE_QUERY,
  SYNTHESIS_PROVENANCE_QUERY,
} from "@lib/graphql/queries";
import { useChatStore } from "@lib/stores/chatStore";
import { MessageSquare, RefreshCw } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery } from "urql";
import { ProvenanceGraph } from "./ProvenanceGraph";

export interface NotIncludedItemData {
  artefactType: string;
  artefactId: string;
  label: string;
  subLabel: string | null;
  reason: string;
  inputRole: string;
}

export interface ProvenanceNodeData {
  id: string;
  artefactType: string;
  artefactId: string | null;
  label: string;
  subLabel: string | null;
  versionNumber: number | null;
  origin: string | null;
  actorSource: string | null;
  aiGenerated: boolean | null;
  generatedAt: string | null;
  stale: boolean;
  staleReasons: string[];
  inputRole: string | null;
  provenanceQuality: "RECORDED" | "INFERRED";
}

export interface ProvenanceGraphData {
  quality: "RECORDED" | "INFERRED";
  generationRun: {
    id: string;
    workflowName: string;
    modelUsed: string | null;
    finishedAt: string | null;
    triggerSource: string | null;
    chatThreadId: string | null;
    user: { id: string; fullName: string | null };
    backgroundTask: { id: string; kind: string; title: string; state: string } | null;
  } | null;
  nodes: ProvenanceNodeData[];
  edges: Array<{
    id: string;
    from: string;
    to: string;
    kind: "INPUT" | "PRODUCED" | "EVIDENCE";
    state: "CURRENT" | "STALE";
    sourceExcerpt: string | null;
  }>;
  notIncluded: NotIncludedItemData[];
  notIncludedCount: number;
}

export type HowDeterminedSubject =
  | { kind: "assessment" }
  | { kind: "synthesis"; candidateSelectionStepId: string }
  | { kind: "decision_summary"; campaignId: string };

interface HowDeterminedPanelProps {
  open: boolean;
  onClose: () => void;
  campaignCandidateId?: string;
  candidateName?: string;
  subject: HowDeterminedSubject;
}

const ROLE_HEADINGS: Record<string, string> = {
  role_blueprint: "Role blueprint",
  assessment_matrix: "Assessment matrix",
  brief: "Brief",
  synthesis: "Round syntheses",
  contribution: "Interview contributions",
  cv_extraction: "CV extraction",
  prior_version: "Prior version",
  prior_assessment: "Prior assessments",
  capability_dimension: "Capability dimensions",
  step_assessment_plan: "Step assessment plan",
  selection_step: "Selection steps",
  candidate_step: "Candidate steps",
  evidence_source: "Evidence sources",
  interview_pack: "Interview pack",
};

const REASON_HEADINGS: Record<string, string> = {
  created_after_generation: "Added after generation",
  synthesis_not_ready: "Pending synthesis",
  not_recorded: "Cannot confirm inclusion",
};

function humaniseRole(role: string): string {
  return ROLE_HEADINGS[role] ?? role.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

function humaniseReason(reason: string): string {
  return REASON_HEADINGS[reason] ?? reason.replace(/_/g, " ");
}

function nodeLink(
  node: ProvenanceNodeData,
  campaignId: string,
  campaignCandidateId?: string,
): string | null {
  switch (node.artefactType) {
    case "RoleBlueprintVersion":
      return `/campaigns/${campaignId}/define/blueprint`;
    case "CampaignBriefVersion":
      return `/campaigns/${campaignId}/define/brief`;
    case "AssessmentMatrixVersion":
    case "AssessmentMatrixBand":
      return `/campaigns/${campaignId}/define/assessment`;
    case "StepAssessmentPlanVersion":
    case "SelectionStep":
      return `/campaigns/${campaignId}/define/selection-process`;
    case "InterviewContribution":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}#contributions`
        : null;
    case "CandidateAssessmentVersion":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}#assessment`
        : null;
    case "InterviewRoundSynthesisVersion":
    case "CandidateSelectionStep":
      return campaignCandidateId
        ? `/campaigns/${campaignId}/candidates/${campaignCandidateId}`
        : null;
    default:
      return null;
  }
}

function QualityTag({ quality }: { quality: "RECORDED" | "INFERRED" }) {
  if (quality === "RECORDED") {
    return (
      <span className="rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700 dark:bg-green-900/20 dark:text-green-300">
        recorded
      </span>
    );
  }
  return (
    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
      reconstructed
    </span>
  );
}

function resolveQuery(subject: HowDeterminedSubject) {
  switch (subject.kind) {
    case "assessment":
      return ASSESSMENT_PROVENANCE_QUERY;
    case "synthesis":
      return SYNTHESIS_PROVENANCE_QUERY;
    case "decision_summary":
      return DECISION_SUMMARY_PROVENANCE_QUERY;
  }
}

function extractProvenance(
  // biome-ignore lint/suspicious/noExplicitAny: urql query result shape varies by subject kind
  data: any,
  subject: HowDeterminedSubject,
): { provenance: ProvenanceGraphData | null; campaignId: string; candidateId: string } {
  if (subject.kind === "decision_summary") {
    const campaign = data?.campaign;
    return {
      provenance: campaign?.decisionSummary?.provenance ?? null,
      campaignId: campaign?.id ?? "",
      candidateId: "",
    };
  }
  const cc = data?.campaignCandidate;
  const campaignId = cc?.campaign?.id ?? "";
  const candidateId = cc?.candidate?.id ?? "";
  if (subject.kind === "assessment") {
    return {
      provenance: cc?.candidateAssessment?.currentVersion?.provenance ?? null,
      campaignId,
      candidateId,
    };
  }
  const step = cc?.candidateSelectionSteps?.find(
    (s: { id: string }) => s.id === subject.candidateSelectionStepId,
  );
  return {
    provenance: step?.interviewRoundSynthesis?.currentVersion?.provenance ?? null,
    campaignId,
    candidateId,
  };
}

export function HowDeterminedPanel({
  open,
  onClose,
  campaignCandidateId,
  candidateName,
  subject,
}: HowDeterminedPanelProps) {
  const [activeTab, setActiveTab] = useState<"details" | "graph">("details");
  const navigate = useNavigate();

  const queryVariables =
    subject.kind === "decision_summary" ? { id: subject.campaignId } : { id: campaignCandidateId };
  const [{ data, fetching }] = useQuery({
    query: resolveQuery(subject),
    variables: queryVariables,
    pause: !open,
  });

  const { provenance, campaignId, candidateId } = extractProvenance(data, subject);

  const produced = provenance?.nodes.find((n) => n.inputRole == null) ?? null;
  const inputs = provenance?.nodes.filter((n) => n.inputRole != null) ?? [];
  const inputsByRole = new Map<string, ProvenanceNodeData[]>();
  for (const node of inputs) {
    const role = node.inputRole as string;
    const group = inputsByRole.get(role) ?? [];
    group.push(node);
    inputsByRole.set(role, group);
  }
  const run = provenance?.generationRun ?? null;

  const notIncluded = provenance?.notIncluded ?? [];
  const notIncludedByReason = new Map<string, NotIncludedItemData[]>();
  for (const item of notIncluded) {
    const group = notIncludedByReason.get(item.reason) ?? [];
    group.push(item);
    notIncludedByReason.set(item.reason, group);
  }

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={
        candidateName ? `How was this determined? — ${candidateName}` : "How was this determined?"
      }
    >
      {fetching && <p className="text-xs text-text-muted">Loading provenance…</p>}

      {!fetching && !provenance && (
        <p className="text-xs text-text-muted">No provenance available for this artefact.</p>
      )}

      {provenance && (
        <div className="space-y-5" data-testid="how-determined-content">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {produced && (
                <span className="text-sm font-medium text-text-primary">{produced.label}</span>
              )}
              {produced?.versionNumber != null && (
                <VersionChip versionNumber={produced.versionNumber} />
              )}
              {produced && (
                <OriginBadge
                  origin={produced.origin}
                  actorSource={produced.actorSource}
                  model={run?.modelUsed}
                />
              )}
            </div>

            {run && (
              <p className="text-[11px] text-text-faint">
                {run.finishedAt && <>{new Date(run.finishedAt).toLocaleString()} &middot; </>}
                Triggered by {run.user.fullName ?? "unknown"}
                {run.triggerSource && <> via {run.triggerSource.replace(/_/g, " ")}</>}
                {run.chatThreadId && (
                  <>
                    {" "}
                    &middot;{" "}
                    <button
                      type="button"
                      className="inline-flex items-center gap-0.5 text-accent underline decoration-dotted underline-offset-2 hover:text-accent-hover"
                      data-testid="open-conversation-link"
                      onClick={() => {
                        useChatStore.getState().setCurrentThread(run.chatThreadId!);
                        useChatStore.getState().open();
                      }}
                    >
                      <MessageSquare size={10} />
                      Open conversation
                    </button>
                  </>
                )}
              </p>
            )}

            {run?.backgroundTask && (
              <p className="text-[11px] text-text-faint">
                Background task: {run.backgroundTask.title} &middot;{" "}
                <span className="font-medium">{run.backgroundTask.state}</span>
              </p>
            )}

            {provenance.quality === "INFERRED" && (
              <div
                className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
                data-testid="provenance-inferred-note"
              >
                Inputs reconstructed from links — this version predates input recording.
              </div>
            )}
          </div>

          <div className="border-b border-border">
            <nav className="-mb-px flex gap-4" data-testid="provenance-tabs">
              <button
                type="button"
                onClick={() => setActiveTab("details")}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "details" ? "border-accent text-accent" : "border-transparent text-text-muted hover:text-text-primary hover:border-border"}`}
                data-testid="tab-details"
              >
                Details
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("graph")}
                className={`pb-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "graph" ? "border-accent text-accent" : "border-transparent text-text-muted hover:text-text-primary hover:border-border"}`}
                data-testid="tab-graph"
              >
                Graph
              </button>
            </nav>
          </div>

          {activeTab === "details" && (
            <>
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Inputs
                </h3>
                {inputsByRole.size === 0 && (
                  <p className="text-xs text-text-muted">No inputs could be determined.</p>
                )}
                <div className="space-y-3">
                  {[...inputsByRole.entries()].map(([role, nodes]) => (
                    <div key={role}>
                      <h4 className="mb-1 text-[11px] font-medium text-text-secondary">
                        {humaniseRole(role)}
                        {nodes.length > 1 && (
                          <span className="text-text-faint"> ×{nodes.length}</span>
                        )}
                      </h4>
                      <ul className="space-y-1">
                        {nodes.map((node) => {
                          const link = nodeLink(node, campaignId, campaignCandidateId);
                          return (
                            <li
                              key={node.id}
                              className="flex flex-wrap items-center gap-2 rounded border border-border px-2 py-1.5 text-xs"
                              data-testid="provenance-input-row"
                            >
                              {link ? (
                                <Link
                                  to={link}
                                  className="text-text-primary underline-offset-2 hover:underline"
                                >
                                  {node.label}
                                </Link>
                              ) : (
                                <span className="text-text-primary">{node.label}</span>
                              )}
                              {node.subLabel && (
                                <span className="text-[10px] text-text-faint">{node.subLabel}</span>
                              )}
                              <VersionChip versionNumber={node.versionNumber} />
                              {node.stale && (
                                <span title={node.staleReasons.join(", ")}>
                                  <StaleBadge />
                                </span>
                              )}
                              <QualityTag quality={node.provenanceQuality} />
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {notIncluded.length > 0 && (
                <div data-testid="not-included-section">
                  <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                    Not included
                  </h3>
                  <div className="space-y-3">
                    {[...notIncludedByReason.entries()].map(([reason, items]) => (
                      <div key={reason}>
                        <h4 className="mb-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                          {humaniseReason(reason)}
                        </h4>
                        <ul className="space-y-1">
                          {items.map((item) => (
                            <li
                              key={`${item.artefactType}:${item.artefactId}`}
                              className="flex flex-wrap items-center gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs dark:border-amber-900/40 dark:bg-amber-900/10"
                              data-testid="not-included-row"
                            >
                              <span className="text-text-primary">{item.label}</span>
                              {item.subLabel && (
                                <span className="text-[10px] text-text-faint">{item.subLabel}</span>
                              )}
                              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                {humaniseReason(item.reason)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  <RegenerateCTA
                    subject={subject}
                    campaignId={campaignId}
                    candidateId={candidateId}
                    onClose={onClose}
                  />
                </div>
              )}
            </>
          )}

          {activeTab === "graph" && (
            <ProvenanceGraph
              provenance={provenance}
              campaignId={campaignId}
              campaignCandidateId={campaignCandidateId}
              onNavigate={(path) => {
                navigate(path);
                onClose();
              }}
            />
          )}
        </div>
      )}
    </SlideOverPanel>
  );
}

function RegenerateCTA({
  subject,
  campaignId,
  candidateId,
  onClose,
}: {
  subject: HowDeterminedSubject;
  campaignId: string;
  candidateId: string;
  onClose: () => void;
}) {
  const [, assessCandidate] = useMutation(ASSESS_CANDIDATE);
  const [, regenerateSynthesis] = useMutation(REGENERATE_INTERVIEW_ROUND_SYNTHESIS);
  const [, regenerateDecisionSummary] = useMutation(REGENERATE_CAMPAIGN_DECISION_SUMMARY);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{
    warning: string;
    deepLinkPath: string | null;
    deepLinkLabel: string | null;
  } | null>(null);

  const handleRegenerate = async (confirmOverwrite = false) => {
    setRegenerating(true);
    setError(null);

    if (subject.kind === "assessment") {
      const result = await assessCandidate({
        input: { campaignId, candidateId },
      });
      const payload = result.data?.assessCandidate;
      if (payload?.errors?.length) {
        setError(payload.errors[0]);
        setRegenerating(false);
        return;
      }
      setRegenerating(false);
      onClose();
    } else if (subject.kind === "decision_summary") {
      const result = await regenerateDecisionSummary({
        input: { campaignId },
      });
      const payload = result.data?.regenerateCampaignDecisionSummary;
      if (payload?.errors?.length) {
        setError(payload.errors[0]);
        setRegenerating(false);
        return;
      }
      setRegenerating(false);
      onClose();
    } else {
      const result = await regenerateSynthesis({
        input: {
          candidateSelectionStepId: subject.candidateSelectionStepId,
          confirmOverwrite,
        },
      });
      const payload = result.data?.regenerateInterviewRoundSynthesis;
      if (payload?.errors?.length) {
        setError(payload.errors[0]);
        setRegenerating(false);
        return;
      }
      if (payload?.status === "manual_edits_present" && !confirmOverwrite) {
        setConfirmState({
          warning: payload.warning ?? "Manual edits will be replaced.",
          deepLinkPath: payload.deepLinkPath ?? null,
          deepLinkLabel: payload.deepLinkLabel ?? null,
        });
        setRegenerating(false);
        return;
      }
      setRegenerating(false);
      onClose();
    }
  };

  const buttonLabel =
    subject.kind === "assessment"
      ? "Regenerate assessment"
      : subject.kind === "decision_summary"
        ? "Regenerate summary"
        : "Regenerate synthesis";

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => handleRegenerate(false)}
        disabled={regenerating}
        className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
        data-testid="regenerate-cta"
      >
        <RefreshCw className="h-3 w-3" />
        {buttonLabel}
      </button>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <RegenerateConfirmDialog
        open={!!confirmState}
        warning={confirmState?.warning ?? ""}
        deepLinkPath={confirmState?.deepLinkPath}
        deepLinkLabel={confirmState?.deepLinkLabel}
        onCancel={() => setConfirmState(null)}
        onConfirm={() => {
          setConfirmState(null);
          handleRegenerate(true);
        }}
      />
    </div>
  );
}
